// @ts-nocheck
import _ from 'lodash';
import React, { forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import omitEmpty from 'omit-empty';
import {
  makeStyles,
  Box,
  Grid,
  Icon,
  Button,
  Typography,
} from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useIfMounted } from '../../../../common/hooks';
import { DateUtils } from '../../../../common/utils';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
  },
  form: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormGroup-root': {
      minHeight: '192px',
      paddingTop: '4px',
    },
  },
  formContent: {
    flex: '1',
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(1, 3),
    overflow: 'auto',
  },
  checkbox: {
    marginTop: '6px',
    pointerEvents: 'none',
  },
  switch: {
    margin: '-4px 0 0 0',
    alignItems: 'flex-start',
    '& .MuiSwitch-root': {
      marginLeft: '-8px',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginRight: '0',
    },
  },
}));

const PromotionPeriodForm = React.forwardRef((props, ref) => {
  const [t] = useTranslation();
  const { control, errors, formState, reset, register, trigger, getValues, handleSubmit } =
    useForm();
  const classes = useStyles();
  const ifMounted = useIfMounted();

  const {
    promotionId,
    periods,
    onSave,
    onCancel,
    actionsDisabled,
    editMode,
    cancelButtonText,
    cancelButtonIcon,
    saveButtonText,
    saveButtonIcon,
  } = props;

  const { isDirty, isSubmitted, isSubmitting } = formState;
  const onSubmit = async data => {
    const formData = omitEmpty(data);

    if (periods?.id) {
      formData.id = periods.id;
    }
    formData.promotionId = promotionId;
    formData.startDate = _.defaultTo(periods?.startDate, data.startDate);
    formData.endDate = _.defaultTo(periods?.endDate, data.endDate);
    try {
      await onSave(formData);
      ifMounted(() => reset(data));
    } catch (e) {
      // Error already handled
    }
  };

  const validateStartDate = value => {
    const endDate = getValues('endDate');
    if (!DateUtils.isValid(value)) {
      return t('promotion.promotionPeriodStartDateInvalid');
    }
    if (DateUtils.compareDates(value, endDate) > 0) {
      return t('promotion.promotionPeriodStartDateAfterEndDate');
    }
    return true;
  };

  const handleStartDateChange = (params, value) => {
    params.onChange(_.defaultTo(value?.format('YYYY-MM-DDTHH:mm:ssZ'), ''));
    if (isSubmitted) {
      trigger('endDate');
    }
  };

  const validateEndDate = value => {
    const startDate = getValues('startDate');
    if (!DateUtils.isValid(value)) {
      return t('promotion.promotionPeriodEndDateInvalid');
    }
    if (DateUtils.compareDates(value, startDate) < 0) {
      return t('promotion.promotionPeriodEndDateBeforeStartDate');
    }
    return true;
  };

  const handleEndDateChange = (params, value) => {
    params.onChange(_.defaultTo(value?.format('YYYY-MM-DDTHH:mm:ssZ'), ''));
    if (isSubmitted) {
      trigger('startDate');
    }
  };
  
  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit(onSubmit)(),
  }));

  return (
    <Box id="PromotionPeriodForm-container" className={classes.container}>
      <form id="PromotionPeriodForm-form" className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <Box className={classes.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                name="startDate"
                control={control}
                defaultValue={_.defaultTo(
                  periods
                    ? DateUtils.format(
                        periods.startDate?.replace(/(\[.+\])/g, ''),
                        'YYYY-MM-DDTHH:mm:ssZ'
                      )
                    : null,
                  DateUtils.format(DateUtils.now('YYYY-MM-DD'), 'YYYY-MM-DDTHH:mm:ssZ')
                )}
                rules={{
                  required: t('promotion.promotionPeriodStartDateRequired'),
                  validate: validateStartDate,
                }}
                render={params => (
                  <KeyboardDatePicker
                    id="PromotionPeriodForm-startDate"
                    label={t('promotion.promotionPeriodStartDate')}
                    variant="inline"
                    inputVariant="outlined"
                    format="DD-MM-YYYY"
                    value={params.value}
                    onChange={value => handleStartDateChange(params, value)}
                    error={!!errors.startDate}
                    helperText={errors.startDate?.message}
                    disabled={editMode}
                    autoOk
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="endDate"
                control={control}
                defaultValue={_.defaultTo(
                  periods
                    ? DateUtils.format(
                        periods.endDate?.replace(/(\[.+\])/g, ''),
                        'YYYY-MM-DDTHH:mm:ssZ'
                      )
                    : null,
                  DateUtils.format(DateUtils.now('YYYY-MM-DD'), 'YYYY-MM-DDTHH:mm:ssZ')
                )}
                rules={{
                  required: t('promotion.promotionPeriodEndDateRequired'),
                  validate: validateEndDate,
                }}
                render={params => (
                  <KeyboardDatePicker
                    id="PromotionPeriodForm-endDate"
                    label={t('promotion.promotionPeriodEndDate')}
                    variant="inline"
                    inputVariant="outlined"
                    format="DD-MM-YYYY"
                    value={params.value}
                    onChange={value => handleEndDateChange(params, value)}
                    error={!!errors.endDate}
                    helperText={errors.endDate?.message}
                    autoOk
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <Box id='PromotionPeriodForm-actions' className={classes.actions}>
          <Button
            id="PromotionPeriodForm-cancel"
            className={classes.button}
            startIcon={<Icon>{_.defaultTo(cancelButtonIcon, 'cancel')}</Icon>}
            onClick={onCancel}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {_.defaultTo(cancelButtonText, t('common.cancel'))}
            </Typography>
          </Button>
          <Button
            id="PromotionPeriodForm-save"
            type="submit"
            color="primary"
            className={classes.button}
            startIcon={<Icon>{_.defaultTo(saveButtonIcon, 'save')}</Icon>}
            disabled={actionsDisabled || isSubmitting || !isDirty}
          >
            <Typography variant="button" noWrap>
              {_.defaultTo(saveButtonText, t('common.create'))}
            </Typography>
          </Button>
        </Box>
      </form>
    </Box>
  );
});

PromotionPeriodForm.defaultProps = {
  periods: undefined,
  cancelButtonText: undefined,
  cancelButtonIcon: undefined,
  saveButtonText: undefined,
  saveButtonIcon: undefined,
  actionsDisabled: undefined,
  editMode: undefined,
};

PromotionPeriodForm.propTypes = {
  promotionId: PropTypes.number.isRequired,
  periods: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  })),
  cancelButtonText: PropTypes.string,
  cancelButtonIcon: PropTypes.string,
  saveButtonText: PropTypes.string,
  saveButtonIcon: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool,
  editMode: PropTypes.bool,
};

export default PromotionPeriodForm;
